import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const TermsOfService = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="legal-page">
      <Header />
      <div className="legal-header">
        <div className="legal-header-content">
          <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 64, height: 64, background: '#5E6B39', borderRadius: 18, marginBottom: 20, fontSize: 30 }}>📄</div>
          <h1>Terms of Service</h1>
          <p style={{ fontSize: 18, color: '#B5B0A5', margin: '14px 0 6px' }}>Please read these terms carefully.</p>
          <p className="legal-date">Last updated: December 2025</p>
        </div>
      </div>

      <div className="legal-content">
        <section className="legal-section">
          <h2>1. Introduction</h2>
          <p>
            Welcome to Custodian AI Automation. These Terms of Service ("Terms") govern your use of our 
            AI voice automation services and website (collectively, the "Services") operated by 
            Custodian AI Automation ("we", "our", or "us"), a business based in Auckland, New Zealand.
          </p>
          <p>
            By accessing or using our Services, you agree to be bound by these Terms. If you do not 
            agree to these Terms, please do not use our Services.
          </p>
        </section>

        <section className="legal-section">
          <h2>2. Definitions</h2>
          <ul>
            <li><strong>"Services"</strong> means our AI voice automation solutions, website, demos, and related services</li>
            <li><strong>"User", "you", or "your"</strong> refers to any individual or entity using our Services</li>
            <li><strong>"Content"</strong> means any information, data, text, or materials provided through our Services</li>
            <li><strong>"AI Voice Agent"</strong> means our automated voice response systems including but not limited to Elma</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>3. Eligibility</h2>
          <p>
            To use our Services, you must:
          </p>
          <ul>
            <li>Be at least 18 years of age</li>
            <li>Have the legal capacity to enter into binding contracts</li>
            <li>Not be prohibited from using our Services under New Zealand law or any other applicable jurisdiction</li>
          </ul>
          <p>
            If you are using our Services on behalf of a business, you represent that you have the 
            authority to bind that business to these Terms.
          </p>
        </section>

        <section className="legal-section">
          <h2>4. Services Description</h2>
          <p>
            Custodian AI Automation provides AI-powered voice automation solutions for businesses, including:
          </p>
          <ul>
            <li>AI voice receptionist and call handling</li>
            <li>Automated appointment booking</li>
            <li>Customer enquiry management</li>
            <li>Voice-based business automation</li>
          </ul>
          <p>
            We reserve the right to modify, suspend, or discontinue any aspect of our Services at any 
            time without prior notice.
          </p>
        </section>

        <section className="legal-section">
          <h2>5. User Obligations</h2>
          <p>When using our Services, you agree to:</p>
          <ul>
            <li>Provide accurate and complete information</li>
            <li>Use the Services only for lawful purposes</li>
            <li>Not interfere with or disrupt the Services</li>
            <li>Not attempt to gain unauthorised access to our systems</li>
            <li>Not use the Services to transmit harmful, offensive, or illegal content</li>
            <li>Comply with all applicable New Zealand laws and regulations</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>6. Bookings and Appointments</h2>
          <h3>6.1 Demo Bookings</h3>
          <p>
            When you book a demo through our website, you agree to:
          </p>
          <ul>
            <li>Provide accurate contact information</li>
            <li>Attend the scheduled appointment or provide reasonable notice of cancellation</li>
            <li>Engage in good faith during the demo session</li>
          </ul>

          <h3>6.2 Cancellation</h3>
          <p>
            We appreciate at least 24 hours' notice for any cancellations or rescheduling. 
            Repeated no-shows may result in restrictions on future bookings.
          </p>
        </section>

        <section className="legal-section">
          <h2>7. Intellectual Property</h2>
          <h3>7.1 Our Intellectual Property</h3>
          <p>
            All intellectual property rights in our Services, including but not limited to software, 
            AI technology, trademarks, logos, and content, are owned by or licensed to Custodian AI 
            Automation. You may not copy, modify, distribute, or create derivative works without our 
            prior written consent.
          </p>

          <h3>7.2 Your Content</h3>
          <p>
            You retain ownership of any content you provide to us. By providing content, you grant us 
            a non-exclusive, worldwide licence to use, store, and process that content for the purpose 
            of providing our Services.
          </p>
        </section>

        <section className="legal-section">
          <h2>8. Consumer Guarantees</h2>
          <p>
            Under the Consumer Guarantees Act 1993, if you are acquiring our Services for personal, 
            domestic, or household use, you have certain guarantees that cannot be excluded. These include:
          </p>
          <ul>
            <li>Services will be provided with reasonable care and skill</li>
            <li>Services will be fit for the purpose for which they are supplied</li>
            <li>Services will be completed within a reasonable time</li>
          </ul>
          <p>
            If you are acquiring our Services for business purposes, you agree that the Consumer 
            Guarantees Act 1993 does not apply to the extent permitted by law.
          </p>
        </section>

        <section className="legal-section">
          <h2>9. Fair Trading</h2>
          <p>
            We comply with the Fair Trading Act 1986. We will not engage in misleading or deceptive 
            conduct, and all representations about our Services will be accurate and truthful.
          </p>
        </section>

        <section className="legal-section">
          <h2>10. Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by New Zealand law:
          </p>
          <ul>
            <li>Our total liability to you for any claims arising from these Terms or your use of our 
            Services shall not exceed the amount you paid to us in the 12 months preceding the claim</li>
            <li>We shall not be liable for any indirect, incidental, special, consequential, or punitive damages</li>
            <li>We shall not be liable for any loss of profits, revenue, data, or business opportunities</li>
          </ul>
          <p>
            Nothing in these Terms excludes or limits our liability for death or personal injury caused 
            by our negligence, fraud, or any other liability that cannot be excluded by law.
          </p>
        </section>

        <section className="legal-section">
          <h2>11. Indemnification</h2>
          <p>
            You agree to indemnify and hold harmless Custodian AI Automation, its directors, employees, 
            and agents from any claims, damages, losses, or expenses (including legal fees) arising from:
          </p>
          <ul>
            <li>Your breach of these Terms</li>
            <li>Your use of our Services</li>
            <li>Your violation of any third-party rights</li>
            <li>Your violation of any applicable laws</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>12. Privacy</h2>
          <p>
            Your privacy is important to us. Our collection, use, and disclosure of personal information 
            is governed by our <Link to="/privacy">Privacy Policy</Link>, which forms part of these Terms. 
            By using our Services, you consent to our privacy practices as described in our Privacy Policy.
          </p>
        </section>

        <section className="legal-section">
          <h2>13. AI Disclaimer</h2>
          <p>
            Our AI voice agents are designed to assist with call handling and customer interactions. 
            Please note:
          </p>
          <ul>
            <li>AI responses are automated and may not always be accurate or complete</li>
            <li>AI agents cannot provide legal, medical, financial, or professional advice</li>
            <li>Users should verify important information independently</li>
            <li>AI technology has limitations and may occasionally produce unexpected results</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>14. Termination</h2>
          <p>
            We may terminate or suspend your access to our Services at any time, with or without cause, 
            and with or without notice. Upon termination:
          </p>
          <ul>
            <li>Your right to use the Services will immediately cease</li>
            <li>We may delete your data in accordance with our Privacy Policy</li>
            <li>Any provisions of these Terms that should survive termination shall remain in effect</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>15. Dispute Resolution</h2>
          <p>
            If a dispute arises between you and Custodian AI Automation:
          </p>
          <ol>
            <li><strong>Negotiation:</strong> We will first attempt to resolve the dispute through good faith negotiation</li>
            <li><strong>Mediation:</strong> If negotiation fails, we agree to attempt mediation through a mutually agreed mediator</li>
            <li><strong>Legal Proceedings:</strong> If mediation fails, either party may pursue legal remedies</li>
          </ol>
          <p>
            Nothing in this clause prevents either party from seeking urgent injunctive relief from a court.
          </p>
        </section>

        <section className="legal-section">
          <h2>16. Governing Law</h2>
          <p>
            These Terms are governed by and construed in accordance with the laws of New Zealand. 
            You agree to submit to the exclusive jurisdiction of the courts of New Zealand for the 
            resolution of any disputes.
          </p>
        </section>

        <section className="legal-section">
          <h2>17. Changes to Terms</h2>
          <p>
            We reserve the right to modify these Terms at any time. Changes will be effective immediately 
            upon posting to our website. Your continued use of our Services after any changes indicates 
            your acceptance of the modified Terms.
          </p>
          <p>
            We encourage you to review these Terms periodically for any updates.
          </p>
        </section>

        <section className="legal-section">
          <h2>18. Severability</h2>
          <p>
            If any provision of these Terms is found to be invalid or unenforceable, that provision 
            shall be limited or eliminated to the minimum extent necessary, and the remaining provisions 
            shall continue in full force and effect.
          </p>
        </section>

        <section className="legal-section">
          <h2>19. Entire Agreement</h2>
          <p>
            These Terms, together with our Privacy Policy, constitute the entire agreement between you 
            and Custodian AI Automation regarding your use of our Services and supersede any prior 
            agreements or understandings.
          </p>
        </section>

        <section className="legal-section">
          <h2>20. Contact Us</h2>
          <p>
            If you have any questions about these Terms of Service, please contact us:
          </p>
          <div className="contact-info">
            <div className="contact-item">
              <Mail size={18} />
              <span>hello@custodianai.co.nz</span>
            </div>
            <div className="contact-item">
              <Phone size={18} />
              <span>+64 210 851 048</span>
            </div>
            <div className="contact-item">
              <MapPin size={18} />
              <span>Auckland, New Zealand</span>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
};

export default TermsOfService;
